import { Collection, IModel, IAttribute, IViolation, IValidationContext } from '../base/annotation';
import { Metamodel } from '../base/model';
import { NamedArray } from '../base/namedarray';
import { Column } from './column';
import { Parameter } from '../base/parameter';
import { LinkedService } from '../linkedservice/linkedservice';
import { Owned } from '../base/named';
import { ParameterValue } from '../base/parameter-value';
import { IPropertyBag } from '../../extensibility/property-bag';
import { UniqueValidator } from '../base/validators';
import { HierarchicalNode } from './hierarchicalnode';
import { TaggableNamed } from '../base/taggable-named';
import { IParameterizable } from '../../interfaces/IParameterizable';
import { OwnedSet } from '../base/ownedset';
import { Folder } from '../folder/folder';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { NamedValidations } from '../base/named-validations';
import { ActionConstants } from '../../constants/action-constants';
import { ValidationConstants } from '../../shared/validation-constants';
import { format } from '../../shared/utils/string.util';
import { ModelConstants } from '../model-constants';
import { GlobalVariables } from '../../constants/globals';
import { Constants } from '../../shared/shared-constants';
import { ParameterConfig } from '../pipeline/parameter-config';
import { Copier } from '../base/copier';

type LinkedServiceValidator = (dataset: Dataset, value: LinkedService, context: IValidationContext) => Promise<IViolation[] | undefined>;

interface IDatasetValidators {
    linkedServiceValidator: LinkedServiceValidator;
}

export const datasetValidators: IDatasetValidators = {
    linkedServiceValidator: undefined
};

export interface IVersioningDataset extends Dataset {
    version: string
    shouldHonorConnectionVersion?: () => boolean;
}
@Metamodel.model({
    name: ModelConstants.dataset,
    display: [shellStringResources.Dataset, shellStringResources.Datasets],
    reusable: true,
    abstract: false,
    referable: true,
    constraints: {
        name: {
            required: true,
            patterns: [
                {
                    message: format(shellStringResources.Validator_NamePattern_StartEnd_Alphanumeric, shellStringResources.Dataset),
                    regex: ValidationConstants.startEndWithAlphaNumericValidator
                },
                {
                    message: format(shellStringResources.Validator_NameMaxLength, shellStringResources.Dataset, ValidationConstants.maxEntityNameLength.toString()),
                    regex: ValidationConstants.factoryEntityNameLengthValidator
                },
                {
                    message: format(shellStringResources.Validator_NameMinLength, shellStringResources.Dataset, ValidationConstants.minDatasetNameLength.toString()),
                    regex: ValidationConstants.datasetNameMinLengthValidator
                },
                {
                    message: format(shellStringResources.Validator_NamePattern_All_AlphanumericUnderscore, shellStringResources.Dataset),
                    regex: ValidationConstants.alphanumericUnderscore
                }
            ],
            uniqueModelName: true,
            validator: NamedValidations.KeyWordsValidator
        }
    },
    accessPublishConstraint: {
        writeResource: {
            action: ActionConstants.datasetsWrite
        },
        deleteResource: {
            action: ActionConstants.datasetsDelete
        }
    }
})
export class Dataset extends TaggableNamed implements IParameterizable {
    public static readonly MODEL: IModel;
    public static readonly COLUMNS: IAttribute;
    public static readonly LINKED_SERVICE_PARAMETERS: IAttribute;
    public static readonly LINKED_SERVICE_PROPERTIES: IAttribute;

    @Metamodel.attribute({
        reference: true,
        type: LinkedService.MODEL,
        constraint: { // Constraint should be redefined to be of a type except IR version check
            required: true,
            validator: (dataset: Dataset, linkedService: LinkedService, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> => {
                if (datasetValidators.linkedServiceValidator) {
                    return datasetValidators.linkedServiceValidator(dataset, linkedService, context);
                }
                return undefined;
            }
        }
    })
    public linkedService: LinkedService;

    @Metamodel.attribute({
        reference: true,
        type: LinkedService.MODEL
    })
    public dmtsCredentialForArtifact?: LinkedService;

    /**
     * Parameter value for referenced linked service.
     */
    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            required: false
        },
        association: 'LINKED_SERVICE_PARAMETERS'
    })
    public linkedServiceParameters: ParameterValue[] = [];

    /**
     * Addition properties for referenced linked service in fabric.
     */
    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            required: false
        },
        association: 'LINKED_SERVICE_PROPERTIES'
    })
    public linkedServiceProperties: ParameterConfig[];

    /*
        Ordered collection of columns. This is not just a bag of columns.
        Deterministic ordering is important. Splicing and insert at a position is important
    */
    @Metamodel.attribute({
        type: Column.MODEL,
        collection: Collection.Array,
        constraint: {
            validator: Dataset.validateColumns
        },
        association: 'COLUMNS',
        templatable: true,
        display: shellStringResources.Schema
    })
    public columns = NamedArray.create<Column>(this);

    // store the native schema for a dataset
    public schemas?: HierarchicalNode;

    @Metamodel.attribute({
        type: Parameter.MODEL,
        collection: Collection.Set,
        constraint: {
            unique: true,
            validator: Dataset.validateParameters
        }
    })
    public readonly parameters = OwnedSet.create<Parameter>(this);

    public folder: Folder = Owned.create(Folder.MODEL, this);

    public isSampleDataset: boolean = false;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public rawHierarchicalSchema?: { [key: string]: any };
    public isV2: boolean;
    public storeReadSettings: IPropertyBag = {}; // cache data for query, for V2 only
    public formatReadSettings: IPropertyBag = {}; // cache data for query, for V2 only
    /**
     * In Trident, some DMTS connections map to multiple ADF `linkedService`. This property is used to specify
     * `linkedServiceType` for these connections.
     */
    public customLinkedServiceType?: string;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.raw = `{
            "properties": {
                "typeProperties": {
                }
            }
        }`;
    }

    public get hasLegacyDependency(): boolean {
        return this.linkedService?.isLegacyConnector;
    }

    public get hasSapCdcDependency(): boolean {
        return false;
    }

    public static validateParameters(dataset: Dataset): IViolation[] | undefined {
        const violations: IViolation[] = [];
        dataset.parameters.forEach((parameter) => {
            if (GlobalVariables.strictParamValidation && !Constants.alphaNumericUnderscoreValidation.test(parameter.name)) {
                violations.push({
                    context: parameter,
                    message: () => format(shellStringResources.Validator_NamePattern_All_AlphanumericUnderscore, parameter.name)
                });
            }
        });
        return violations;
    }

    public static async validateColumns(dataset: Dataset, columns: NamedArray<Column>, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> {
        if (!dataset.isV2 && columns != null) {
            // validate columns only for non-datasetv2
            return UniqueValidator.INSTANCE.validate({}, Dataset.MODEL.attribute('columns'), columns, dataset, context);
        }
        return undefined;
    }

    public recoverDatasetConfiguration(
        dataset: Dataset,
        previouseDataset: Dataset,
        config: { splitTablePath?: boolean } = { }
    ): void {}

    public clear(): void {
        super.clear();
        this.parameters.clear();
    }

    public getLinkedServiceType(): string {
        return this.customLinkedServiceType ?? this.linkedService?.getLinkedServiceType();
    }

    public onDatasetCloned(dataset: Dataset, context: { inlineDataset?: boolean }): void {
        if (GlobalVariables.isTrident) {
            if (this.linkedService?.parameterized || this.dmtsCredentialForArtifact?.parameterized) {
                if (this.linkedService != null) {
                    dataset.linkedService = Copier.clone(
                        context,
                        [this.linkedService],
                        undefined,
                        true
                    )[0] as LinkedService;
                }
                if (this.dmtsCredentialForArtifact != null) {
                    dataset.dmtsCredentialForArtifact = Copier.clone(
                        context,
                        [this.dmtsCredentialForArtifact],
                        undefined,
                        true
                    )[0] as LinkedService;
                }
            }
        }
    }
}
